import React, { useState, useRef } from 'react';
import Onboarding from './components/Onboarding';
import Chat from './components/Chat';
import Results from './components/Results';
import ErrorBoundary from './components/ErrorBoundary';
import { useRecaptcha } from './hooks/useRecaptcha';
import { SAMPLE_SYNTHESIS } from './test-fixtures/sampleSynthesis';
import './App.css';

// Dev/testing: ?test=results jumps straight to Results view with sample data (no chat needed)
const isTestResults = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.has('test') && params.get('test') === 'results';
};

function App() {
  const [view, setView] = useState(() => (isTestResults() ? 'results' : 'onboarding')); // 'onboarding' | 'chat' | 'results'
  const [sessionState, setSessionState] = useState(() => (isTestResults() ? { sessionId: 'test-results' } : null));
  const [conversationHistory, setConversationHistory] = useState([]);
  const [sessionError, setSessionError] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [synthesisContent, setSynthesisContent] = useState(() => (isTestResults() ? SAMPLE_SYNTHESIS : null));
  const { executeRecaptcha } = useRecaptcha();
  const isStartingRef = useRef(false);

  const handleStartChat = async () => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    setSessionError(null);
    setSessionLoading(true);
    try {
      const token = await executeRecaptcha('start_conversation');
      const response = await fetch('/api/session/new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recaptcha_token: token })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create session');
      }

      setSessionState(data.sessionState);
      setSessionError(null);
      try {
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'chat_start');
        }
      } catch (e) { /* Firefox iOS may throw from gtag */ }
      // Defer view change to next tick - avoids Firefox iOS "Script error" when Chat mounts synchronously
      setTimeout(() => setView('chat'), 0);
    } catch (error) {
      console.error('Error starting session:', error);
      const isNetworkError = error.message === 'Failed to fetch' ||
        (error.message && error.message.toLowerCase().includes('network'));
      const message = isNetworkError
        ? 'We couldn\'t connect to our service. Please check your connection and try again. If it still doesn\'t work, email us at hello@xf.works.'
        : (error.message || 'Apologies—we couldn\'t start your session. Please wait and try again in a few minutes, or email us at hello@xf.works.');
      setSessionError(message);
    } finally {
      setSessionLoading(false);
      isStartingRef.current = false;
    }
  };

  const handleSynthesisReady = (content, history) => {
    setSynthesisContent(content);
    setConversationHistory(history);
    setView('results');
  };

  const footer = (
    <footer className="app-footer" role="contentinfo">
      <p>© 2026 Experience Futures Holdings</p>
      <p>
        <span className="text-muted" style={{ fontSize: '11px' }}>
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          {' '}and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          {' '}apply.
        </span>
        {process.env.NODE_ENV !== 'production' && (
          <>
            {' · '}
            <a href="?test=results" className="text-muted" style={{ fontSize: '12px' }}>Test Results</a>
          </>
        )}
      </p>
    </footer>
  );

  return (
    <div className="App">
      <main id="main-content" className="App-main" role="main">
        {view === 'onboarding' && (
          <div className="app-scroll">
            {sessionError && (
              <div className="error-box" style={{ marginBottom: '1rem' }}>
                <p className="text-content">
                  <span className="text-error">error:</span> {sessionError}
                </p>
                <div className="onboarding-nav mt-4">
                  <div className="nav-buttons">
                    <button type="button" className="terminal-button" onClick={() => setSessionError(null)}>
                      [dismiss]
                    </button>
                  </div>
                </div>
              </div>
            )}
            <Onboarding onStart={handleStartChat} loading={sessionLoading} />
            {footer}
          </div>
        )}
        {view === 'results' && (
          <Results
            synthesisContent={synthesisContent}
            conversationHistory={conversationHistory}
            sessionState={sessionState}
          />
        )}
        {view === 'chat' && (
          sessionError ? (
            <div className="chat-container">
              <div className="terminal-frame chat-frame">
                <div className="terminal-titlebar">XF - Personal IP Discovery Tool</div>
                <div className="terminal-content" style={{ padding: '1.5rem' }}>
                  <div className="error-box">
                    <p className="text-content">
                      <span className="text-error">error:</span> {sessionError}
                    </p>
                    <div className="onboarding-nav mt-4">
                      <div className="nav-buttons">
                        <button
                          type="button"
                          className="terminal-button"
                          onClick={() => setSessionError(null)}
                        >
                          [retry]
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <ErrorBoundary>
              <Chat
                sessionState={sessionState}
                setSessionState={setSessionState}
                conversationHistory={conversationHistory}
                setConversationHistory={setConversationHistory}
                onSynthesisReady={handleSynthesisReady}
              />
            </ErrorBoundary>
          )
        )}
      </main>
    </div>
  );
}

export default App;
